/**
 * The skill effect registry: each skill effect (a `SkillDef.fx` id) draws itself from a file of its
 * own in this folder, `render/fx/<fx>.ts`, which calls `registerFx` when it loads. Effects
 * (render/effects.ts) loads every file here and hands each move's events to the handlers of its
 * effect, keeping only what all of them share: particles, flashes, the screen shake and the routing
 * of world events.
 *
 * Adding an effect is one new file: `registerFx('myFx', (host) => ({ skill(c) { … } }))`, drawing
 * through the `FxHost` (the Effects it runs in). State an effect keeps between events (a chain under
 * way, foes frozen in ice) lives in the factory's closure, one per Effects.
 */
import type { Entity, SkillDef, SkillFx, World } from '@nine/shared';
import type { Sfx } from '../../audio';
import type { IsoCamera } from '../camera';
import type { AnimState } from '../effects';
import type { SheetSpec } from '../fxSheets';
import type { SkillLook, SwingStyle } from '../skillFx';
import type { SpriteBank } from '../sprites';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  t: number;
  life: number;
  c: string;
  /** Pixel size (1 = single pixel). */
  s?: number;
  /** Drawn as a short line along its velocity (sparks, wind). */
  streak?: boolean;
  /** Velocity damping per second (0 = none). */
  drag?: number;
}

export type Fx =
  | {
      type: 'arc';
      x: number;
      y: number;
      t: number;
      life: number;
      f: number;
      a0: number;
      a1: number;
      rx: number;
      ry: number;
      w: number;
      col: string;
    }
  | { type: 'thrust'; x: number; y: number; t: number; life: number; f: number; len: number; col: string }
  | { type: 'bolt'; x: number; y: number; t: number; life: number; seed: number }
  | { type: 'ring'; x: number; y: number; t: number; life: number; r: number; col: string }
  | { type: 'gather'; x: number; y: number; t: number; life: number; r: number; col: string }
  | { type: 'pillar'; x: number; y: number; t: number; life: number; col: string }
  | { type: 'yinyang'; x: number; y: number; t: number; life: number; r: number }
  | { type: 'sigil'; x: number; y: number; t: number; life: number; r: number; col: string }
  /** Thick expanding shockwave with a glowing band. */
  | { type: 'shock'; x: number; y: number; t: number; life: number; r: number; col: string }
  /** Cracks radiating across the ground from an impact. */
  | { type: 'crack'; x: number; y: number; t: number; life: number; r: number; seed: number }
  /** A column of light rising from the caster. */
  | { type: 'beam'; x: number; y: number; t: number; life: number; w: number; col: string }
  /** Radial speed lines bursting out of an impact point. */
  | { type: 'lines'; x: number; y: number; t: number; life: number; r: number; col: string; seed: number }
  | { type: 'star'; x: number; y: number; t: number; life: number; r: number; col: string }
  /** A jagged crack of lightning between two points, flickering (col: rgb). */
  | {
      type: 'zap';
      x: number;
      y: number;
      t: number;
      life: number;
      x1: number;
      y1: number;
      col: string;
      seed: number;
      /** Core colour (rgb) instead of white: black for Haki-like lightning. */
      core?: string;
      /** Width of the glow. */
      w?: number;
    }
  /** A puff of cloud swelling and thinning out (the cloud step of เหยียบเมฆา). */
  | { type: 'puff'; x: number; y: number; t: number; life: number; r: number }
  /** Sword qi: a large glowing crescent flying from (x, y) to (x1, y1) with a fading trail. */
  | {
      type: 'crescent';
      x: number;
      y: number;
      t: number;
      life: number;
      x1: number;
      y1: number;
      r: number;
      col: string;
    }
  /** A razor-thin flash of light along a straight cut (a warp slash). */
  | { type: 'cut'; x: number; y: number; t: number; life: number; x1: number; y1: number; col: string }
  | { type: 'shield'; x: number; y: number; t: number; life: number; f: number; h: number; col: string }
  | {
      type: 'wave';
      x: number;
      y: number;
      t: number;
      life: number;
      x1: number;
      y1: number;
      col: string;
    }
  /**
   * A cached pixel-art effect (fxSheets.ts) played over its life; flying to (x1, y1) when given.
   * `ground`: laid on the ground, drawn under the figures (`drawGround`).
   */
  | {
      type: 'sheet';
      x: number;
      y: number;
      t: number;
      life: number;
      s: SheetSpec;
      x1?: number;
      y1?: number;
      ground?: boolean;
    }
  /**
   * A skill's own drawing (fx/*.ts), drawn in its place among the effects: `x`, `y` are its
   * position with the view offset added, `ox`, `oy` the offset, and `k` how far through its life.
   */
  | {
      type: 'custom';
      x: number;
      y: number;
      t: number;
      life: number;
      /** Drawn on the ground, under the figures (C0-VFX ink/sword effects; its own pass after the turn, effects.ts). */
      ground?: boolean;
      draw: (g: CanvasRenderingContext2D, x: number, y: number, k: number, ox: number, oy: number) => void;
    }
  | {
      type: 'ghost';
      x: number;
      y: number;
      t: number;
      life: number;
      img: HTMLCanvasElement;
      flip: boolean;
      ax: number;
      ay: number;
      /** Locked split-body look: solid for 35 % of the life, then 0.2, then 0.08 alpha (taiji sample). */
      step?: boolean;
    };

export interface VisualSword {
  srcId: number;
  tgtId: number;
  t: number;
  launch: number;
  arrive: number;
  ang: number;
  x: number;
  y: number;
  fx: number;
  fy: number;
  rot: number;
  /** Thrown poison needles instead of flying swords. */
  needle?: boolean;
}

/** What a skill effect draws with: the Effects it runs in (render/effects.ts). */
export interface FxHost {
  readonly world: World;
  readonly iso: IsoCamera;
  readonly sprites: SpriteBank;
  readonly sfx: Sfx;
  fx: Fx[];
  parts: Particle[];
  swords: VisualSword[];
  shake: number;
  /** Frost creeping in round the screen, 0–1, fading. */
  frost: number;
  anim(id: number): AnimState;
  /** Where the fighter stands on screen. */
  pos(e: Entity): [number, number];
  /** The figure's height over the original 56 px design (1 for beasts). */
  tall(e: Entity | undefined | null): number;
  /** Height of the figure on screen (world pixels). */
  figH(e: Entity): number;
  /** RGB of the element a move belongs to. */
  elemColor(skillId: string): string;
  startSwing(src: Entity, style: SwingStyle, charged: boolean): void;
  swingArc(src: Entity, style: SwingStyle, col: string, scale?: number): void;
  bite(src: Entity): Fx;
  burst(x: number, y: number, n: number, cols: string[], spd?: number, up?: number, life?: number): void;
  sparks(x: number, y: number, dir: number, n: number, cols: string[], spd?: number): void;
  dust(x: number, y: number, n: number, spread: number, spd?: number): void;
  bloodSpray(x: number, y: number, n: number, spd: number): void;
  snowBurst(x: number, y: number, n: number, ice: boolean): void;
  impact(col: string, flash: number, punch: number, shake: number): void;
  cinematic(src: Entity, strength: number, hold: number, zoom?: boolean): void;
}

/** A move used (the 'skill' event), after the effects every move shares. */
export interface SkillCast {
  src: Entity;
  srcId: number;
  tgt: Entity | undefined;
  s: SkillDef;
  look: SkillLook;
  /** The swing the move plays. */
  style: SwingStyle;
  /** The caster on screen, and where the move plays out (the target, the spot picked, or the caster). */
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  /** The spot picked, for a ground-targeted move. */
  spot: { x: number; y: number } | undefined;
  volley: { tgtId: number; at: number }[] | undefined;
  /** The caster has strike poses (a figure, not a beast). */
  humanoid: boolean;
  /** A break (破). */
  brk: boolean;
}

/** A move's blow landing for damage (the 'hit' event with a source and damage above 0). */
export interface SkillHit {
  src: Entity;
  tgt: Entity;
  /** The foe's feet and chest height on screen. */
  x: number;
  y: number;
  cy: number;
  /** Not dodged. */
  landed: boolean;
}

export interface SkillFxHandlers {
  /** The move used: its own effect on top of the shared ones. */
  skill?(c: SkillCast): void;
  /** Its blow landing; an effect with this draws its own mark in place of the shared slash. */
  hit?(h: SkillHit): void;
  /** A warp ('blink' event), in place of the shared warp slash. */
  blink?(e: Entity, tgt: Entity | null, fromX: number, fromY: number, s: SkillDef): void;
  /** Every frame, after the effects have aged and before the spent ones are dropped. */
  step?(dt: number): void;
  /** Drawn right after a figure (renderer.ts via `Effects.drawHeld`), at its feet. */
  drawHeld?(g: CanvasRenderingContext2D, e: Entity, x: number, y: number): void;
}

export type SkillFxFactory = (host: FxHost) => SkillFxHandlers;

const FACTORIES: { ids: readonly SkillFx[]; make: SkillFxFactory }[] = [];
const TAKEN = new Set<SkillFx>();

/** Registers the effect drawn for one or more `SkillDef.fx` ids (once each). */
export function registerFx(ids: SkillFx | readonly SkillFx[], make: SkillFxFactory): void {
  const list: readonly SkillFx[] = typeof ids === 'string' ? [ids] : ids;
  for (const id of list) {
    if (TAKEN.has(id)) throw new Error(`skill fx '${id}' registered twice`);
    TAKEN.add(id);
  }
  FACTORIES.push({ ids: list, make });
}

/** The registered effects made for one Effects. */
export class SkillFxSet {
  private readonly byFx = new Map<SkillFx, SkillFxHandlers>();
  private readonly all: SkillFxHandlers[] = [];

  constructor(host: FxHost) {
    for (const { ids, make } of FACTORIES) {
      const h = make(host);
      for (const id of ids) this.byFx.set(id, h);
      this.all.push(h);
    }
  }

  /** The handlers of a skill's effect (none for an effect with no file of its own). */
  of(fx: SkillFx | undefined): SkillFxHandlers | undefined {
    return fx === undefined ? undefined : this.byFx.get(fx);
  }

  /** Steps every effect that keeps something going between events. */
  step(dt: number): void {
    for (const h of this.all) h.step?.(dt);
  }

  /** Draws whatever each effect holds round a figure. */
  drawHeld(g: CanvasRenderingContext2D, e: Entity, x: number, y: number): void {
    for (const h of this.all) h.drawHeld?.(g, e, x, y);
  }
}
